<script setup lang="ts">
import {computed} from 'vue';
import Button from "primevue/button";
import {TimeAllocationCategoryDTO} from '../../stores/timeAllocationCategories.store.ts';
import {PulseTimeAllocation} from '../../types/DataTypes.ts';
import {useTimeAllocationColors} from '../../composables/useTimeAllocationColors';

const props = defineProps<{
  segments: Array<{
    category: TimeAllocationCategoryDTO;
    value: number;
    leftPosition: number;
    width: number;
    color: string;
  }>;
  localAllocation: PulseTimeAllocation;
  disabled?: boolean;
  canDecrease: (key: string) => boolean;
  canIncrease: (key: string) => boolean;
}>();

const emit = defineEmits<{
  adjustPercentage: [categoryKey: string, direction: 'increase' | 'decrease'];
  disableCategory: [key: string];
}>();

const { getCategoryColor } = useTimeAllocationColors();
</script>

<template>
  <div class="space-y-2">
    <TransitionGroup
        name="category"
        tag="div"
        class="grid grid-cols-1 md:grid-cols-2 gap-2"
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 transform scale-95 translate-y-2"
        enter-to-class="opacity-100 transform scale-100 translate-y-0"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100 transform scale-100 translate-y-0"
        leave-to-class="opacity-0 transform scale-95 -translate-y-2"
        move-class="transition-transform duration-300 ease-out"
    >
      <div
          v-for="segment in segments"
          :key="`detail-${segment.category.key}`"
          class="flex 2xl:items-center gap-3 p-2 rounded-lg flex-row xl:flex-col 2xl:flex-row justify-between"
      >
        <div class="flex flex-row items-center gap-2">
        <!-- Color indicator -->
        <div 
          class="w-4 h-4 rounded-full flex-shrink-0"
          :style="{ backgroundColor: getCategoryColor(segment.category.key) }"
        ></div>
        
        <!-- Category info -->
        <div class="flex-1 min-w-0">
          <span class="text-sm font-medium">{{ segment.category.label }}</span>
        </div>
        </div>
        <div class="flex flex-row">
        <!-- Percentage adjustment controls -->
          <div class="flex items-center gap-1">
            <!-- Decrease button -->
            <Button
                v-if="!disabled && canDecrease(segment.category.key)"
                icon="pi pi-minus"
                severity="secondary"
                text
                size="small"
                class="p-1 w-6 h-6"
                @click="emit('adjustPercentage', segment.category.key, 'decrease')"
            />
            
            <!-- Value -->
            <span class="text-sm font-mono font-medium min-w-[3rem] text-center">{{ segment.value }}%</span>
            
            <!-- Increase button -->
            <Button
                v-if="!disabled && canIncrease(segment.category.key)"
                icon="pi pi-plus"
                severity="secondary"
                text
                size="small"
                class="p-1 w-6 h-6"
                @click="emit('adjustPercentage', segment.category.key, 'increase')"
            />
          </div>

          <!-- Remove button -->
          <Button
              v-if="!disabled"
              icon="pi pi-times"
              severity="secondary"
              text
              size="small"
              class="p-1 w-6 h-6"
              @click="emit('disableCategory', segment.category.key)"
          />
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.category-enter-active,
.category-leave-active {
  transition: all 0.3s ease;
}

.category-enter-from {
  opacity: 0;
  transform: scale(0.95) translateY(8px);
}

.category-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-8px);
}

.category-move {
  transition: transform 0.3s ease;
}
</style>
